if (!customElements.get('product-info')) {
  class ProductInfo extends HTMLElement {
    constructor() {
      super();
      this.productForm = this.querySelector('product-form');
      this.productFormQuantity = this.productForm?.querySelector('input[name="quantity"]');
      this.quantityInput = this.querySelector('quantity-input');
      this.quantityInputElement = this.quantityInput?.querySelector('input[name="quantity"]');
      this.quantityInputElement?.addEventListener('change', () => {
        this.handleQuantityInput();
      });

      // get the Frame pop-up buttons and add event listeners
      this.framePopUpOnATCButtons = this.querySelectorAll('[data-frame-pop-up-on-atc-button]');
      if (this.framePopUpOnATCButtons.length > 0) {
        this.framePopUpOnATCButtons.forEach((button) => {
          button.addEventListener('click', this.handleFramePopUpButtonClick.bind(this));
        });
      }

      // get the Frame type options toggle + add event listeners
      this.frameViewPrimary = this.querySelector('[data-frame-view-primary]');
      this.frameViewSecondary = this.querySelector('[data-frame-view-secondary]');
      if (this.frameViewPrimary && this.frameViewSecondary) {
        [this.frameViewPrimary, this.frameViewSecondary].forEach((view) => {
          view.addEventListener('click', (event) => this.handleFrameViewToggleClick(event));
        });

        // select Canvas if the url has framing=canvas, when it's not in 'Edit' mode
        if (
          sessionStorage.productEdit == undefined &&
          new URLSearchParams(window.location.search).get('framing') === 'canvas'
        ) {
          this.setFrameView(true);
        }
      }

      this.init();
    }

    /**
     * init
     * @description Set the right product info on init in the sessionStorage and handle the page leave on edit mode
     */
    init() {
      // return if there's no product being edited
      if (sessionStorage.productEdit == undefined) return;

      const clearEdit = sessionStorage.clearEdit;
      sessionStorage.removeItem('clearEdit');

      // clear the Edit mode if the user decided to leave the page
      if (clearEdit == 'true' || clearEdit == undefined) {
        sessionStorage.removeItem('productEdit');
        sessionStorage.removeItem('newProductEdit');
      } else {
        // change Add to cart button text to "Save changes"
        const addButton = document
          .getElementById(`product-form-${this.dataset.section}`)
          ?.querySelector('[name="add"]');
        const addToCartButtonText = addButton?.querySelector('[data-add-to-cart-button-text]');
        if (addToCartButtonText)
          addToCartButtonText.textContent = window.variantStrings.saveChanges;
      }

      // get the current product being edited
      this.currentProductEdit = sessionStorage.productEdit;

      // warn the user about losing changes when leaving the page if there's a product being edited
      this.handlePageLeaveOnEditMode();

      // return if there's no new product being edited
      if (sessionStorage.newProductEdit == undefined) return;

      // update the productEdit if the user is coming from an Edit mode and clicked on another product to edit
      sessionStorage.productEdit = sessionStorage.newProductEdit;
      sessionStorage.removeItem('newProductEdit');
    }

    /**
     * handleQuantityInput
     * @description Set the quantity from the quantity input web component to the product form web component.
     */
    handleQuantityInput() {
      this.productFormQuantity.value = this.quantityInputElement.value;
    }

    /**
     * handlePageLeaveOnEditMode
     * @description Show the browser default dialog with the "Changes you made may not be saved." warning when leaving the page
     */
    handlePageLeaveOnEditMode() {
      window.addEventListener('beforeunload', (event) => {
        /**
         * Show pop-up when a product is being edited on the current page and it's changed to another page
         * - document.querySelector('cart-items').currentProductEdit - it checks if there was a product being edited before clicking on a new one to be edited. This variable is updated on the Edit icon click on the Cart.
         * - this.currentProductEdit - it checks if there's a product being edited on the Product page itself. This variable is added on load and updated on ATC.
         */
        if (
          document.querySelector('cart-items').currentProductEdit != undefined ||
          (document.querySelector('cart-items').currentProductEdit == undefined &&
            this.currentProductEdit != undefined)
        ) {
          event.preventDefault();
        }
      });
    }

    /**
     * handleFramePopUpButtonClick
     * @description handle the Frame pop-up buttons click event - Choose frame (the frame drawer opens) or Continue without frame (proceeds with the ATC form submission)
     */
    handleFramePopUpButtonClick(event) {
      const body = document.querySelector('body');
      const typeOfAction =
        event.currentTarget.dataset.framePopUpOnAtcButton == 'proceed' ? 'proceed' : 'choose_frame';

      if (typeOfAction == 'proceed') {
        // continue the form submission
        this.productForm.querySelector('[type="submit"]').click();
      } else {
        // show the Frame options drawer
        body.classList.add('upsell-products-drawer-is-open', 'upsell-products-1');
      }
    }

    /**
     * handleFrameViewToggleClick
     * @description Handle click on the Art Print / Canvas toggle. Toggling `frames-secondary-view` on <product-info>
     */
    handleFrameViewToggleClick(event) {
      const isCanvasView = event.currentTarget === this.frameViewSecondary;
      this.setFrameView(isCanvasView);

      // re-query on every click, since the currently-connected <product-upsell-products> may be a different instance than the 1 present at page load.
      document.querySelector('product-upsell-products')?.handleFrameViewToggle(isCanvasView);
    }

    /**
     * setFrameView
     * @description Toggle the Canvas view class and aria-pressed in sync with the Art Print / Canvas buttons
     * @param {Boolean} isCanvasView
     */
    setFrameView(isCanvasView) {
      this.classList.toggle('frames-secondary-view', isCanvasView);
      this.frameViewPrimary?.setAttribute('aria-pressed', !isCanvasView);
      this.frameViewSecondary?.setAttribute('aria-pressed', isCanvasView);
    }
  }
  customElements.define('product-info', ProductInfo);
}

/**
 * ProductFormButtons
 * @description Toggle the "sticky" class based on the intersection. This will make the ATC sticky on mobile.
 */
class ProductFormButtons extends HTMLElement {
  connectedCallback() {
    this.initIntersectionObserver();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  initIntersectionObserver() {
    // initialize on screens smaller than 768px
    if (window.innerWidth >= 768) return;

    // add margin bottom to the main footer to avoid the ATC hiding footer content
    const mainFooter = document.querySelector('[data-main-footer]');
    mainFooter?.classList.add('max-md:mb-[5.375rem]');

    // toggle the "sticky" class based on if it's intersecting and it's past the ATC
    this.observer = new IntersectionObserver((entries) => {
      const hasScrolledPastAddToCart = this.getBoundingClientRect().bottom < 0;

      entries.forEach((entry) => {
        this.classList.toggle(
          'product-form__buttons--sticky',
          !entry.isIntersecting && hasScrolledPastAddToCart
        );
      });
    });

    this.observer.observe(this);
  }
}

customElements.define('product-form-buttons', ProductFormButtons);
